import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSearch,
  Loader2,
  Lock,
  Unlock,
  Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import { getDocumentContentBlob } from "@/lib/api/documents";
import { useUploadDocument } from "@/lib/api/documents.queries";
import type {
  AcceptMuiscaReviewItemInput,
  MuiscaCoverageState,
  MuiscaDocument,
  MuiscaDocumentKind,
  MuiscaDocumentSource,
  MuiscaExtractionStatus,
  MuiscaIntakeResult,
  MuiscaProposal,
  MuiscaReviewItem,
  MuiscaReviewKind,
} from "@/lib/api/muisca";
import {
  useAcceptMuiscaReviewItem,
  useClassifyMuiscaDocument,
  useMuiscaDocuments,
  useMuiscaReviewItems,
  useMuiscaYears,
  useRejectMuiscaReviewItem,
  useSetMuiscaYearCompleted,
} from "@/lib/api/muisca.queries";
import type { TaxPaymentProof } from "@/lib/api/taxes";
import { useActiveCompany } from "@/lib/company-context";
import {
  useActiveTeam,
  useCanApproveTaxDeclarations,
} from "@/lib/team-context";
import {
  MAX_UPLOAD_BYTES,
  formatBytes,
  resolveContentType,
} from "@/lib/document-files";
import { fmtDate } from "@/components/inventory/format";
import { MuiscaConsentCard } from "@/components/taxes/MuiscaConsentCard";
import { MuiscaCredentialCard } from "@/components/taxes/MuiscaCredentialCard";
import { MuiscaJobsCard } from "@/components/taxes/MuiscaJobsCard";
import { saveBlob } from "@/components/radian/format";

export const Route = createFileRoute("/companies/$companyId/impuestos/muisca")({
  component: MuiscaPage,
});

const ACCEPT_ATTR = ".pdf,.xlsx,.xls,.xml,.json,.zip,.csv";

const KIND_LABEL: Record<MuiscaDocumentKind, string> = {
  rut: "RUT",
  form_110: "Formulario 110",
  form_210: "Formulario 210",
  form_300: "Formulario 300",
  form_350: "Formulario 350",
  form_490: "Recibo 490",
  form_310: "Formulario 310",
  form_420: "Formulario 420",
  form_160: "Formulario 160",
  form_260: "Formulario 260",
  form_2593: "Formulario 2593",
  form_2516: "Formato 2516",
  form_other: "Otro formulario",
  exogena_receipt_f10006: "Recibo F10006",
  exogena_xml: "XML exogena",
  third_party_report: "Reporte de terceros",
  acquisition_report: "Reporte de adquisiciones",
  rub_report: "Beneficiarios finales",
  authorization_f1876: "Autorizacion de numeracion",
  balances_snapshot: "Saldos de obligaciones",
  filings_inventory: "Inventario de formularios",
  notice: "Comunicacion DIAN",
  manifest: "Manifiesto de recoleccion",
  unknown: "Sin clasificar",
};

const STATUS_LABEL: Record<
  MuiscaExtractionStatus,
  { label: string; variant: "success" | "warning" | "destructive" | "muted" }
> = {
  not_extracted: { label: "Sin extraer", variant: "muted" },
  ok: { label: "Leido", variant: "success" },
  review_required: { label: "Por revisar", variant: "warning" },
  extraction_failed: { label: "Fallo la lectura", variant: "destructive" },
  format_not_parsed: { label: "Formato no leido", variant: "destructive" },
};

const COVERAGE_STATE_LABEL: Record<MuiscaCoverageState, string> = {
  complete: "Completa",
  partial: "Parcial",
  blocked: "Bloqueada",
  none_found: "Sin resultados",
  not_applicable: "No aplica",
  not_checked: "Sin consultar",
};

const REVIEW_KIND_LABEL: Record<MuiscaReviewKind, string> = {
  classification: "Clasificacion",
  identity_mismatch: "Identidad no coincide",
  year_mismatch: "Año no coincide",
  unknown_layout: "Formato desconocido",
  format_not_parsed: "Formato no leido",
  filed_declaration: "Declaracion presentada",
  payment_receipt: "Recibo de pago",
  rut_codes: "Codigos RUT",
  unmatched_event: "Sin evento en el calendario",
};

const SOURCE_LABEL: Record<MuiscaDocumentSource, string> = {
  automated: "Recoleccion automatica",
  manual_upload: "Carga manual",
  manifest: "Manifiesto",
};

const PROOF_LABEL: Record<TaxPaymentProof, string> = {
  paid: "Pagado (sello bancario)",
  receipt_generated: "Recibo generado, pago sin probar",
};

const MUISCA_ERROR_TITLE: Record<string, string> = {
  MUISCA_DOCUMENT_NOT_FOUND: "El documento no esta en el archivo de la empresa",
  MUISCA_DOCUMENT_BYTES_MISSING:
    "El archivo no esta disponible en el almacenamiento",
  MUISCA_INVALID_INPUT: "Datos invalidos",
  MUISCA_REVIEW_ITEM_NOT_FOUND: "El item de revision no existe",
  MUISCA_REVIEW_ITEM_CLOSED: "El item de revision ya fue resuelto",
  MUISCA_PROPOSAL_NOT_ACCEPTABLE: "Este item solo informa: no se puede aceptar",
  MUISCA_TAXPAYER_MISMATCH: "El archivo no pertenece a este contribuyente",
  APPROVAL_REQUIRES_PORTAL_SESSION:
    "La aceptacion requiere la sesion del responsable",
  TAX_FILING_NOT_APPROVED: "La declaracion no tiene visto bueno",
  TAX_FILING_APPROVAL_STALE: "El visto bueno quedo desactualizado",
  TAX_FILING_DUPLICATE_FORM_NUMBER:
    "El numero de formulario ya esta registrado",
  TAX_FILING_EVIDENCE_REQUIRED: "Falta la evidencia de la presentacion",
  TAX_PAYMENT_EVIDENCE_REQUIRED: "Falta el numero del recibo 490",
  TAX_EVENT_NOT_FOUND: "El evento del calendario no existe",
};

function errorToast(fallback: string, err: unknown) {
  const code = err instanceof ApiError ? err.code : undefined;
  const title = (code && MUISCA_ERROR_TITLE[code]) || fallback;
  const description = err instanceof Error ? err.message : undefined;
  toast.error(title, {
    description: description !== title ? description : undefined,
  });
}

function yearOptions(): number[] {
  const current = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) => current - index);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
    reader.readAsDataURL(file);
  });
}

type UploadPhase =
  | { phase: "uploading" | "classifying" }
  | { phase: "done"; result: MuiscaIntakeResult }
  | { phase: "error"; message: string };

type UploadEntry = {
  id: string;
  fileName: string;
  sizeBytes: number;
} & UploadPhase;

function MuiscaPage() {
  return (
    <RequireAuth>
      <AppShell>
        <MuiscaContent />
      </AppShell>
    </RequireAuth>
  );
}

function MuiscaContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const role = useActiveTeam().activeTeam?.role;
  const accountantPlus =
    role === "owner" || role === "admin" || role === "accountant";
  const canApprove = useCanApproveTaxDeclarations();
  const years = useMemo(yearOptions, []);
  const [taxableYear, setTaxableYear] = useState<number>(() =>
    new Date().getFullYear(),
  );

  const coverageQuery = useMuiscaYears(companyId, taxableYear);
  const documentsQuery = useMuiscaDocuments(companyId, {
    year: taxableYear,
    limit: 500,
  });
  const reviewQuery = useMuiscaReviewItems(companyId, {
    status: "open",
    year: taxableYear,
    limit: 500,
  });
  const upload = useUploadDocument(companyId);
  const classify = useClassifyMuiscaDocument(companyId);
  const completeYear = useSetMuiscaYearCompleted(companyId);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const coverage =
    coverageQuery.data?.years.find(
      (year) => year.taxableYear === taxableYear,
    ) ?? null;

  function setPhase(id: string, phase: UploadPhase) {
    setEntries((items) =>
      items.map((item) =>
        item.id === id
          ? {
              id: item.id,
              fileName: item.fileName,
              sizeBytes: item.sizeBytes,
              ...phase,
            }
          : item,
      ),
    );
  }

  async function onFilesPicked(list: FileList | null) {
    if (fileInputRef.current) fileInputRef.current.value = "";
    const files = Array.from(list ?? []);
    if (!files.length || !accountantPlus) return;
    setBusy(true);
    const year = taxableYear;
    for (const file of files) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const base = { id, fileName: file.name, sizeBytes: file.size };
      if (file.size > MAX_UPLOAD_BYTES) {
        setEntries((items) => [
          {
            ...base,
            phase: "error",
            message: `Supera el maximo de 10 MB (${formatBytes(file.size)}).`,
          },
          ...items,
        ]);
        continue;
      }
      const contentType = resolveContentType(file);
      if (!contentType) {
        setEntries((items) => [
          { ...base, phase: "error", message: "Tipo de archivo no soportado." },
          ...items,
        ]);
        continue;
      }
      setEntries((items) => [{ ...base, phase: "uploading" }, ...items]);
      try {
        const contentBase64 = await readFileAsDataUrl(file);
        const vaultDocument = await upload.mutateAsync({
          fileName: file.name,
          contentType,
          contentBase64,
          category: "muisca",
          notes: `MUISCA ${year}`,
        });
        setPhase(id, { phase: "classifying" });
        const result = await classify.mutateAsync({
          documentId: vaultDocument.id,
          taxableYear: year,
        });
        setPhase(id, { phase: "done", result });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "No pudimos procesar el archivo.";
        setPhase(id, { phase: "error", message });
      }
    }
    setBusy(false);
  }

  function toggleCompleted() {
    if (!coverage) return;
    const completed = !coverage.completed;
    completeYear.mutate(
      { year: taxableYear, completed },
      {
        onSuccess: () =>
          toast.success(
            completed
              ? `Año ${taxableYear} marcado como completo`
              : `Año ${taxableYear} reabierto`,
          ),
        onError: (err) => errorToast("No pudimos actualizar el año", err),
      },
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/impuestos"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver a impuestos
          </Link>
          <div className="flex items-center gap-2">
            <FileSearch className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">MUISCA</h1>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Lo que la DIAN tiene de la empresa por año gravable: formularios
            presentados, recibos 490, RUT y reportes. Los archivos se guardan en
            el archivo de la empresa, se clasifican y, con la aceptacion del
            responsable, quedan como evidencia en el calendario. PahFacturar
            nunca presenta ni paga en MUISCA.
          </p>
        </div>
        <div className="grid gap-1.5">
          <label
            className="text-xs font-medium text-muted-foreground"
            htmlFor="muisca-year"
          >
            Año gravable
          </label>
          <Select
            value={String(taxableYear)}
            onValueChange={(value) => setTaxableYear(Number(value))}
          >
            <SelectTrigger id="muisca-year" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <main className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="h-4 w-4 text-primary" />
                Subir originales de {taxableYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                PDF de formularios y recibos, Excel de reportes, XML, JSON, CSV
                o ZIP descargados de MUISCA, hasta 10 MB cada uno. Cada archivo
                se guarda en el archivo de la empresa y se clasifica para el año
                elegido.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={(e) => void onFilesPicked(e.target.files)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || !accountantPlus}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Seleccionar archivos
                </Button>
                {!accountantPlus ? (
                  <p className="text-xs text-muted-foreground">
                    Tu rol puede ver los originales, pero no subirlos.
                  </p>
                ) : null}
              </div>
              {entries.length ? (
                <ul className="divide-y rounded-md border">
                  {entries.map((entry) => (
                    <li key={entry.id} className="space-y-2 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-sm">
                          {entry.phase === "done" ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          ) : entry.phase === "error" ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                          <span className="font-medium">{entry.fileName}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatBytes(entry.sizeBytes)}
                          </span>
                        </div>
                        {entry.phase === "uploading" ? (
                          <Badge variant="muted">Subiendo al archivo</Badge>
                        ) : entry.phase === "classifying" ? (
                          <Badge variant="muted">Clasificando</Badge>
                        ) : entry.phase === "error" ? (
                          <Badge variant="destructive">Error</Badge>
                        ) : null}
                      </div>
                      {entry.phase === "error" ? (
                        <p className="text-sm text-destructive">
                          {entry.message}
                        </p>
                      ) : null}
                      {entry.phase === "done" ? (
                        <IntakeResultView result={entry.result} />
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Originales de {taxableYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documentsQuery.isPending ? (
                <Skeleton className="h-32 rounded-lg" />
              ) : documentsQuery.isError ? (
                <ErrorState
                  title="No pudimos cargar los originales"
                  error={documentsQuery.error}
                  onRetry={() => void documentsQuery.refetch()}
                  retryLabel="Reintentar"
                />
              ) : documentsQuery.data?.documents.length ? (
                <DocumentsTable
                  companyId={companyId}
                  documents={documentsQuery.data.documents}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Aun no hay originales registrados para {taxableYear}.
                </p>
              )}
            </CardContent>
          </Card>
        </main>

        <aside className="space-y-6">
          <MuiscaConsentCard companyId={companyId} canApprove={canApprove} />

          <MuiscaCredentialCard companyId={companyId} canApprove={canApprove} />

          <MuiscaJobsCard
            companyId={companyId}
            accountantPlus={accountantPlus}
            canApprove={canApprove}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Cobertura {taxableYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {coverageQuery.isPending ? (
                <Skeleton className="h-48 rounded-lg" />
              ) : coverageQuery.isError ? (
                <ErrorState
                  title="No pudimos cargar la cobertura"
                  error={coverageQuery.error}
                  onRetry={() => void coverageQuery.refetch()}
                  retryLabel="Reintentar"
                />
              ) : coverage ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {coverage.completed ? (
                      <Badge variant="success">
                        Año completo · {fmtDate(coverage.completed.at)}
                      </Badge>
                    ) : (
                      <Badge variant="outline">Año abierto</Badge>
                    )}
                    {accountantPlus ? (
                      <Button
                        type="button"
                        size="sm"
                        variant={coverage.completed ? "outline" : "default"}
                        onClick={toggleCompleted}
                        disabled={completeYear.isPending}
                      >
                        {coverage.completed ? (
                          <Unlock className="h-3.5 w-3.5" />
                        ) : (
                          <Lock className="h-3.5 w-3.5" />
                        )}
                        {coverage.completed ? "Reabrir" : "Marcar año completo"}
                      </Button>
                    ) : null}
                  </div>
                  {coverage.completed?.note ? (
                    <p className="text-xs text-muted-foreground">
                      {coverage.completed.note}
                    </p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2">
                    <Metric
                      title="Originales"
                      value={String(coverage.documents.total)}
                    />
                    <Metric
                      title="Leidos"
                      value={String(coverage.documents.ok)}
                    />
                    <Metric
                      title="Por revisar"
                      value={String(coverage.documents.reviewRequired)}
                    />
                    <Metric
                      title="Items abiertos"
                      value={String(coverage.openReviewItems)}
                    />
                  </div>
                  {coverage.documents.failed ||
                  coverage.documents.bytesMissing ? (
                    <p className="text-xs text-muted-foreground">
                      {coverage.documents.failed
                        ? `${coverage.documents.failed} con lectura fallida. `
                        : ""}
                      {coverage.documents.bytesMissing
                        ? `${coverage.documents.bytesMissing} sin archivo en el almacenamiento.`
                        : ""}
                    </p>
                  ) : null}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fuente</TableHead>
                        <TableHead>Automatica</TableHead>
                        <TableHead>Manual</TableHead>
                        <TableHead className="text-right">Docs</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coverage.sources.map((source) => (
                        <TableRow key={source.sourceKey}>
                          <TableCell className="text-xs">
                            <span className="flex flex-wrap items-center gap-1.5">
                              {source.label}
                              {source.missing ? (
                                <Badge variant="warning">Falta</Badge>
                              ) : null}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {coverageStateText(source.automated)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {coverageStateText(source.manual)}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {source.documents}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {coverage.gaps.length ? (
                    <Alert variant="warning">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Informacion faltante</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc space-y-1 pl-4">
                          {coverage.gaps.map((gap) => (
                            <li key={gap}>{gap}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Todas las fuentes esperadas tienen documentos o consulta
                      registrada.
                    </p>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>

          <ReviewQueueCard
            companyId={companyId}
            taxableYear={taxableYear}
            items={reviewQuery.data?.items ?? []}
            isPending={reviewQuery.isPending}
            error={reviewQuery.isError ? reviewQuery.error : null}
            onRetry={() => void reviewQuery.refetch()}
            canApprove={canApprove}
            accountantPlus={accountantPlus}
          />
        </aside>
      </div>
    </div>
  );
}

function coverageStateText(
  row: { state: MuiscaCoverageState; errorCode: string | null } | null,
): string {
  if (!row) return "—";
  const label = COVERAGE_STATE_LABEL[row.state];
  return row.errorCode ? `${label} (${row.errorCode})` : label;
}

function periodText(
  original: Pick<MuiscaDocument, "periodYear" | "periodNumber">,
): string {
  const parts = [
    original.periodYear ? String(original.periodYear) : null,
    original.periodNumber,
  ].filter((part): part is string => !!part);
  return parts.length ? parts.join(" · ") : "—";
}

function IntakeResultView(props: {
  result: MuiscaIntakeResult;
  depth?: number;
}) {
  const { result } = props;
  const depth = props.depth ?? 0;
  const status = STATUS_LABEL[result.classification.extractionStatus];
  const proposals = result.reviewItems.filter(
    (item) => item.status === "open" && item.proposal,
  );
  return (
    <div className={depth ? "ml-4 border-l pl-3" : ""}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {depth ? (
          <span className="text-xs text-muted-foreground">
            {result.document.fileName ?? "miembro"}
          </span>
        ) : null}
        <Badge variant="outline">
          {KIND_LABEL[result.classification.kind]}
        </Badge>
        <Badge variant={status.variant}>{status.label}</Badge>
        {result.classification.formNumber ? (
          <span className="text-xs text-muted-foreground">
            No. {result.classification.formNumber}
          </span>
        ) : null}
        {result.classification.filedAt ? (
          <span className="text-xs text-muted-foreground">
            presentado {fmtDate(result.classification.filedAt)}
          </span>
        ) : null}
        {!result.classification.identityVerified ? (
          <Badge variant="warning">NIT no verificado</Badge>
        ) : null}
      </div>
      {result.classification.reviewReasons.length ? (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          {result.classification.reviewReasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
      {proposals.length ? (
        <ul className="mt-1 space-y-0.5 text-xs">
          {proposals.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary">{REVIEW_KIND_LABEL[item.kind]}</Badge>
              <span className="text-muted-foreground">{item.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {result.members.length ? (
        <div className="mt-2 space-y-2">
          {result.members.map((member) => (
            <IntakeResultView
              key={member.document.id}
              result={member}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DocumentsTable(props: {
  companyId: string;
  documents: MuiscaDocument[];
}) {
  const { companyId, documents } = props;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function download(original: MuiscaDocument) {
    if (!original.documentId) return;
    setDownloadingId(original.id);
    try {
      saveBlob(
        await getDocumentContentBlob(companyId, original.documentId),
        original.fileName ?? `${original.id}.bin`,
      );
    } catch (err) {
      errorToast("No pudimos descargar el original", err);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tipo</TableHead>
          <TableHead>Formulario</TableHead>
          <TableHead>Periodo</TableHead>
          <TableHead>Presentado</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Archivo</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((original) => {
          const status = STATUS_LABEL[original.extractionStatus];
          return (
            <TableRow key={original.id}>
              <TableCell>
                <div className="font-medium">{KIND_LABEL[original.kind]}</div>
                <div className="text-xs text-muted-foreground">
                  {SOURCE_LABEL[original.source]}
                  {original.fileName ? ` · ${original.fileName}` : ""}
                </div>
              </TableCell>
              <TableCell>
                {original.formNumber ?? "—"}
                {original.correctionOf ? (
                  <div className="text-xs text-muted-foreground">
                    corrige {original.correctionOf}
                  </div>
                ) : null}
              </TableCell>
              <TableCell>{periodText(original)}</TableCell>
              <TableCell>
                {original.filedAt ? fmtDate(original.filedAt) : "—"}
              </TableCell>
              <TableCell>
                <Badge variant={status.variant}>{status.label}</Badge>
                {original.bytesState === "missing" ? (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Archivo pendiente
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="text-right">
                {original.contentUrl && original.documentId ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void download(original)}
                    disabled={downloadingId === original.id}
                  >
                    {downloadingId === original.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Descargar
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function ReviewQueueCard(props: {
  companyId: string;
  taxableYear: number;
  items: MuiscaReviewItem[];
  isPending: boolean;
  error: Error | null;
  onRetry: () => void;
  canApprove: boolean;
  accountantPlus: boolean;
}) {
  const { companyId, taxableYear, items, canApprove, accountantPlus } = props;
  const accept = useAcceptMuiscaReviewItem(companyId);
  const reject = useRejectMuiscaReviewItem(companyId);
  const [accepting, setAccepting] = useState<MuiscaReviewItem | null>(null);
  const [rejecting, setRejecting] = useState<MuiscaReviewItem | null>(null);
  const [note, setNote] = useState("");
  const [declaredAmount, setDeclaredAmount] = useState("");
  const [proof, setProof] = useState<TaxPaymentProof>("receipt_generated");
  const [eventId, setEventId] = useState("");
  const [amount, setAmount] = useState("");

  function openAccept(item: MuiscaReviewItem) {
    setNote("");
    setDeclaredAmount("");
    setProof("receipt_generated");
    setEventId(
      item.proposal?.type === "payment_receipt"
        ? (item.proposal.eventId ?? "")
        : "",
    );
    setAmount("");
    setAccepting(item);
  }

  function openReject(item: MuiscaReviewItem) {
    setNote("");
    setRejecting(item);
  }

  function submitAccept() {
    if (!accepting?.proposal) return;
    const proposal = accepting.proposal;
    const trimmedNote = note.trim() || undefined;
    let input: AcceptMuiscaReviewItemInput = { note: trimmedNote };
    if (proposal.type === "filed_declaration") {
      const value =
        declaredAmount.trim() === "" ? undefined : Number(declaredAmount);
      if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
        toast.error("Ingresa un valor declarado valido");
        return;
      }
      if (proposal.declaredAmount === null && value === undefined) {
        toast.error(
          "El lector no pudo leer el valor declarado: indicalo para aceptar",
        );
        return;
      }
      input = { note: trimmedNote, declaredAmount: value };
    } else if (proposal.type === "payment_receipt") {
      const value = amount.trim() === "" ? undefined : Number(amount);
      if (value !== undefined && (!Number.isFinite(value) || value <= 0)) {
        toast.error("Ingresa un valor pagado valido");
        return;
      }
      if (proposal.total === null && value === undefined) {
        toast.error(
          "El lector no pudo leer el total del recibo: indicalo para aceptar",
        );
        return;
      }
      const resolvedEventId = eventId.trim() || undefined;
      if (!proposal.eventId && !resolvedEventId) {
        toast.error("Indica el evento del calendario que paga este recibo");
        return;
      }
      input = {
        note: trimmedNote,
        proof,
        eventId: resolvedEventId,
        amount: value,
      };
    }
    accept.mutate(
      { itemId: accepting.id, input },
      {
        onSuccess: (result) => {
          toast.success(
            result.eventId
              ? "Evidencia registrada en el calendario"
              : result.rutCodes
                ? `Codigos RUT actualizados: ${result.rutCodes.join(", ") || "ninguno"}`
                : "Item aceptado",
          );
          setAccepting(null);
        },
        onError: (err) => errorToast("No pudimos aceptar el item", err),
      },
    );
  }

  function submitReject() {
    if (!rejecting) return;
    const trimmed = note.trim();
    if (!trimmed) {
      toast.error("Escribe la razon del rechazo");
      return;
    }
    reject.mutate(
      { itemId: rejecting.id, note: trimmed },
      {
        onSuccess: () => {
          toast.success("Item rechazado");
          setRejecting(null);
        },
        onError: (err) => errorToast("No pudimos rechazar el item", err),
      },
    );
  }

  const acceptingProposal = accepting?.proposal ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Revision de importacion {taxableYear}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {props.isPending ? (
          <Skeleton className="h-32 rounded-lg" />
        ) : props.error ? (
          <ErrorState
            title="No pudimos cargar la cola de revision"
            error={props.error}
            onRetry={props.onRetry}
            retryLabel="Reintentar"
          />
        ) : items.length ? (
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.id} className="space-y-2 rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={item.proposal ? "secondary" : "outline"}>
                    {REVIEW_KIND_LABEL[item.kind]}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {fmtDate(item.createdAt)}
                  </span>
                </div>
                <p className="text-sm">{item.message}</p>
                {item.proposal ? (
                  <ProposalSummary
                    companyId={companyId}
                    proposal={item.proposal}
                  />
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {item.proposal && canApprove ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => openAccept(item)}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Aceptar
                    </Button>
                  ) : null}
                  {accountantPlus ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openReject(item)}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Rechazar
                    </Button>
                  ) : null}
                </div>
                {item.proposal && !canApprove ? (
                  <p className="text-xs text-muted-foreground">
                    Solo un owner o admin del equipo puede aceptar la propuesta.
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No hay items abiertos para {taxableYear}.
          </p>
        )}
      </CardContent>

      <Dialog
        open={accepting !== null}
        onOpenChange={(open) => !open && setAccepting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aceptar propuesta</DialogTitle>
            <DialogDescription>
              {acceptingProposal?.type === "filed_declaration"
                ? "La declaracion queda registrada como presentada en el calendario con el numero de formulario, la fecha y el original detras."
                : acceptingProposal?.type === "payment_receipt"
                  ? "El recibo 490 queda como pago del evento. Solo marca Pagado si respondes por el sello bancario o el saldo en cero."
                  : "Los codigos de responsabilidad leidos del RUT actualizan los de la empresa."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            {acceptingProposal?.type === "filed_declaration" ? (
              <div className="grid gap-1.5">
                <Label htmlFor="muisca-accept-declared">
                  Valor declarado
                  {acceptingProposal.declaredAmount === null
                    ? ""
                    : " (opcional, con nota)"}
                </Label>
                <Input
                  id="muisca-accept-declared"
                  inputMode="decimal"
                  value={declaredAmount}
                  onChange={(e) => setDeclaredAmount(e.target.value)}
                  placeholder={
                    acceptingProposal.declaredAmount === null
                      ? "El lector no pudo leer el valor"
                      : String(acceptingProposal.declaredAmount)
                  }
                />
              </div>
            ) : null}
            {acceptingProposal?.type === "payment_receipt" ? (
              <>
                <div className="grid gap-1.5">
                  <Label htmlFor="muisca-accept-proof">Soporte</Label>
                  <Select
                    value={proof}
                    onValueChange={(value) =>
                      setProof(value as TaxPaymentProof)
                    }
                  >
                    <SelectTrigger id="muisca-accept-proof">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PROOF_LABEL) as TaxPaymentProof[]).map(
                        (value) => (
                          <SelectItem key={value} value={value}>
                            {PROOF_LABEL[value]}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="muisca-accept-event">
                    Evento del calendario
                    {acceptingProposal.eventId ? " (resuelto)" : ""}
                  </Label>
                  <Input
                    id="muisca-accept-event"
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    placeholder="Id del evento que paga el recibo"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="muisca-accept-amount">
                    Valor pagado
                    {acceptingProposal.total === null ? "" : " (opcional)"}
                  </Label>
                  <Input
                    id="muisca-accept-amount"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={
                      acceptingProposal.total === null
                        ? "El lector no pudo leer el total"
                        : String(acceptingProposal.total)
                    }
                  />
                </div>
              </>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="muisca-accept-note">Nota (opcional)</Label>
              <textarea
                id="muisca-accept-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-16 rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Quien acepta y por que, si el valor difiere del leido"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAccepting(null)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={submitAccept}
              disabled={accept.isPending}
            >
              {accept.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rejecting !== null}
        onOpenChange={(open) => !open && setRejecting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar item</DialogTitle>
            <DialogDescription>
              El item queda cerrado con tu nota; el original sigue en el archivo
              de la empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="muisca-reject-note">Razon del rechazo</Label>
            <textarea
              id="muisca-reject-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-16 rounded-md border bg-background px-3 py-2 text-sm"
              placeholder="Por que no se acepta esta lectura"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRejecting(null)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submitReject}
              disabled={reject.isPending || !note.trim()}
            >
              {reject.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ProposalSummary(props: {
  companyId: string;
  proposal: MuiscaProposal;
}) {
  const { companyId, proposal } = props;
  const fmtCop = useMoneyFormatter();
  if (proposal.type === "filed_declaration") {
    return (
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          Formulario {proposal.formCode} No. {proposal.formNumber} · presentado{" "}
          {fmtDate(proposal.filedAt)} · periodo {fmtDate(proposal.period.start)}{" "}
          a {fmtDate(proposal.period.end)}
          {proposal.correctionOf ? ` · corrige ${proposal.correctionOf}` : ""}
        </p>
        <p className="flex flex-wrap items-center gap-1.5">
          Valor declarado:{" "}
          {proposal.declaredAmount === null
            ? "no leido"
            : fmtCop(proposal.declaredAmount)}
          {proposal.effectiveness === "effectiveness_unverified" ? (
            <Badge variant="warning">Eficacia sin verificar</Badge>
          ) : null}
        </p>
        <Link
          to="/companies/$companyId/impuestos/$eventId"
          params={{ companyId, eventId: proposal.eventId }}
          className="text-primary hover:underline"
        >
          Ver evento del calendario
        </Link>
      </div>
    );
  }
  if (proposal.type === "payment_receipt") {
    return (
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          Recibo 490 No. {proposal.receiptNumber}
          {proposal.paidAt ? ` · pagado ${fmtDate(proposal.paidAt)}` : ""}
          {proposal.concept ? ` · ${proposal.concept}` : ""}
          {proposal.period ? ` · periodo ${proposal.period}` : ""}
          {proposal.paysFormNumber
            ? ` · paga el formulario ${proposal.paysFormNumber}`
            : ""}
        </p>
        <p>
          Total: {proposal.total === null ? "no leido" : fmtCop(proposal.total)}
          {proposal.breakdown
            ? ` (impuesto ${fmtCop(proposal.breakdown.impuesto)}, sancion ${fmtCop(proposal.breakdown.sancion)}, intereses ${fmtCop(proposal.breakdown.intereses)})`
            : ""}
        </p>
        {proposal.eventId ? (
          <Link
            to="/companies/$companyId/impuestos/$eventId"
            params={{ companyId, eventId: proposal.eventId }}
            className="text-primary hover:underline"
          >
            Ver evento del calendario
          </Link>
        ) : (
          <p>Sin evento resuelto: indica el evento al aceptar.</p>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p>Codigos casilla 53: {proposal.codes.join(", ") || "ninguno"}</p>
      {proposal.added.length ? (
        <p>Nuevos: {proposal.added.join(", ")}</p>
      ) : null}
      {proposal.removed.length ? (
        <p>Retirados: {proposal.removed.join(", ")}</p>
      ) : null}
      {proposal.ciiuCodes.length ? (
        <p>CIIU: {proposal.ciiuCodes.join(", ")}</p>
      ) : null}
    </div>
  );
}

function Metric(props: { title: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs font-medium text-muted-foreground">{props.title}</p>
      <p className="mt-1 text-lg font-semibold">{props.value}</p>
    </div>
  );
}
